import * as PIXI from "pixi.js";
import { Spine } from "@pixi/spine-pixi";

// Create a SymbolPool class to manage the pool of symbols
class SymbolPool {
  private pool: Symbol[] = [];

  constructor(initialPoolSize: number = 10) {
    for (let i = 0; i < initialPoolSize; i++) {
      this.pool.push(new Symbol("1"));
    }
  }

  public acquire(): Symbol {
    if (this.pool.length > 0) {
      const symbol = this.pool.pop();
      if (symbol) {
        symbol.alpha = 1;
        symbol.position.set(0, 0);
        symbol.scale.set(1);
        symbol.visible = true;
      }
      return symbol!;
    } else {
      return new Symbol("1");
    }
  }

  public release(symbol: Symbol) {
    symbol.parent?.removeChild(symbol);
    this.pool.push(symbol);
  }
}

export class Symbol extends PIXI.Container {
  private type: string | undefined;
  private symbol: Spine;
  private multText: PIXI.Text;
  private multiplier: number = 0;

  public originalPosition!: PIXI.Point;
  public originalParent!: PIXI.Container;

  public static SYMBOL_WIDTH: number = 210; // Default width of the Symbol.
  public static SYMBOL_HEIGHT: number = 245; // Default height of the Symbol.

  private static _pool: SymbolPool;

  public static get pool(): SymbolPool {
    if (!Symbol._pool) {
      throw new Error(
        "Symbol pool is not initialized. Call Symbol.createPool() first.",
      );
    }
    return Symbol._pool;
  }

  public static createPool(initialPoolSize: number = 10) {
    Symbol._pool = new SymbolPool(initialPoolSize);
  }

  constructor(type: string) {
    super();
    this.symbol = Spine.from({
      skeleton: "h1Json",
      atlas: "h1Atlas",
    });

    this.addChild(this.symbol);
    this.multText = new PIXI.Text({
      text: "",
      style: {
        fontSize: 70,
        fontFamily: "CustomFont",
        fill: "0xffdd00",
        fontWeight: "bold",
        stroke: {
          color: 0x000000,
          width: 4,
        },
      },
    });
    this.multText.anchor.set(0.5);
    this.addChild(this.multText);
    this.setType(type.toString());
    this.symbol.state.addListener({
      event: function (entry, event) { },
      complete: (entry) => {
        if (entry.animation) {
          this.emit(entry.animation.name);
        }
      },
      start: () => { },
      end: function (entry) { },
      dispose: function (entry) { },
    });
  }

  getType() {
    return this.type;
  }

  getMultiplier() {
    return this.multiplier;
  }

  setType(type: string) {
    this.removeChild(this.symbol);
    this.symbol.destroy();
    this.type = type;
    let symbolName: string = "";
    this.zIndex = 0;
    this.multText.visible = false;
    switch (this.type) {
      case "1":
        symbolName = "h5";
        break;
      case "2":
        symbolName = "h4";
        break;
      case "3":
        symbolName = "h3";
        break;
      case "4":
        symbolName = "h2";
        break;
      case "5":
        symbolName = "h1";
        break;
      case "6":
        symbolName = "la";
        break;
      case "7":
        symbolName = "lk";
        break;
      case "8":
        symbolName = "lq";
        break;
      case "9":
        symbolName = "lj";
        break;
      case "10":
        symbolName = "wild";
        this.zIndex = 10;
        break;
      case "11":
        symbolName = "bonus";
        this.zIndex = 10;
        break;
      default:
        console.error("Invalid symbol type:" + type);
        break;
    }

    if (symbolName[0] === "l") {
      this.symbol = Spine.from({
        skeleton: "lowJson",
        atlas: "lowAtlas",
      });
      this.symbol.skeleton.setSkinByName(symbolName[1]);
    } else {
      this.symbol = Spine.from({
        skeleton: `${symbolName}Json`,
        atlas: `${symbolName}Atlas`,
      });
    }
    
    this.symbol.scale.set(0.18);
    if (symbolName[0] === "h") {
      this.symbol.scale.set(0.19);
    }
    this.symbol.skeleton.setSlotsToSetupPose();
    this.symbol.y = 0;
    this.symbol.angle = 0;

    if (this.type === "10") {
      // this.symbol.scale.set(0.36);
      this.symbol.y = 50;
      this.symbol.skeleton.setSkinByName("wild");
    }

    this.addChild(this.symbol);
    this.idle();
  }

  setMultiplier(multiplier: number) {
    if (this.type !== "10") {
      console.error("Only wild symbols can have multipliers");
      return;
    }
    this.multiplier = multiplier;
    if (multiplier === 1) {
      this.symbol.skeleton.setSkinByName(`w`);
    } else {
      this.symbol.skeleton.setSkinByName(`x${multiplier}`);
    }

    this.symbol.skeleton.setSlotsToSetupPose();
    // this.symbol.state.setAnimation(0, 'm_idle_on', true);
    this.idle();
  }

  public async win(quickStop?: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.symbol.state.setAnimation(0, "match", false);
      this.symbol.state.addAnimation(
        0,
        this.type === "10" ? "m_idle_on" : "idle",
        true,
      );
      setTimeout(() => {
        resolve();
      }, 1300);
    });
  }

  public land() {
    this.symbol.state.setAnimation(0, "land", false);
    this.symbol.state.addAnimation(0, "idle", true);
  }

  public idle() {
    let animationName = "idle";
    if (this.type === "10") {
      animationName = "m_idle_on";
    }
    // Get the animation duration (in frames or time)
    const animation = this.symbol.skeleton.data.findAnimation(animationName);
    if (animation) {
      const duration = animation.duration;

      // Randomly pick a time between 0 and the animation duration
      const randomTime = Math.random() * duration;

      // Set the animation to play from the random time
      this.symbol.state.setAnimation(0, animationName, true).timeScale = 0.5; // loop is set to true

      this.symbol.state.update(randomTime);
    }
  }
}
